import { Check, ChevronDown, ExternalLink, Hand, Inbox, RotateCcw, Sparkles, Zap } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { QueueEntry, QueueSnapshot } from "../../shared/global-queue";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { useViewerLogin } from "../hooks/useViewerLogin";
import {
  hostedDataStaleSince,
  loadHostedQueueSnapshot,
  preloadHostedPull,
  releaseHostedPullClaim,
  setHostedQueueSkip,
} from "../lib/hosted";
import { queueViewerStatus } from "../lib/queue-status";
import { outcomePriority, pullKey, pullView, repositoryShortName } from "../lib/pull-view";
import { handleLocalNavigation, navigate } from "../lib/navigation";
import { startVisibilityPolling } from "../lib/visibility-polling";
import { AppShell } from "./AppShell";
import { PullBadge, PullFlags, PullMeasures, PullCard } from "./PullPresentation";

type QueueLens = "anything-useful" | "quick-wins";

const skipReasons = ["Not my area", "Too large right now", "Needs a specialist", "Not ready"];

export function orderedEntries(
  entries: QueueEntry[],
  lens: QueueLens,
  locallySkipped: Set<string>,
  justCompletedHead?: string | null,
  justCompletedPull?: string | null,
): QueueEntry[] {
  return entries
    .filter((entry) =>
      entry.forMe.outcome !== "ineligible" &&
      !entry.forMe.skipped &&
      entry.headSha !== justCompletedHead &&
      `${entry.repository}#${entry.pullNumber}` !== justCompletedPull &&
      !locallySkipped.has(`${entry.repository}#${entry.pullNumber}`)
    )
    .sort((left, right) => {
      const priority = outcomePriority[left.forMe.outcome] - outcomePriority[right.forMe.outcome];
      if (priority !== 0) return priority;
      if (lens === "quick-wins") {
        const duration = (left.estimatedMinutes ?? Number.MAX_SAFE_INTEGER) - (right.estimatedMinutes ?? Number.MAX_SAFE_INTEGER);
        if (duration !== 0) return duration;
      }
      return (left.forMe.eligibleRank ?? Number.MAX_SAFE_INTEGER) - (right.forMe.eligibleRank ?? Number.MAX_SAFE_INTEGER)
        || left.position - right.position;
    });
}

function queueReason(entry: QueueEntry): string {
  if (entry.forMe.outcome === "in-review-by-self") return "Your review is already in progress. Pick up with your context intact.";
  if (entry.forMe.outcome === "claimed-by-self") return "You claimed this review. Finish it, or hand it back so somebody else can.";
  if (entry.forMe.outcome === "assigned") return "This review is waiting specifically on you.";
  return entry.reasonDetail || "This is the most useful general-pool review available to you.";
}

export function PersonalQueuePage({ initialSnapshot, local = false }: { initialSnapshot: QueueSnapshot; local?: boolean }) {
  usePageMetadata("Queue · CHEW", "The single best next pull request for you.");
  const viewerLogin = useViewerLogin();
  const [snapshot, setSnapshot] = useState(initialSnapshot);
  const [lens, setLens] = useState<QueueLens>(() => localStorage.getItem("chew:queue-lens") === "quick-wins" ? "quick-wins" : "anything-useful");
  const [locallySkipped, setLocallySkipped] = useState<Set<string>>(() => new Set());
  const [skipOpen, setSkipOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastSkipped, setLastSkipped] = useState<QueueEntry>();
  const [notice, setNotice] = useState<string>();
  const [refreshError, setRefreshError] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const [staleSince, setStaleSince] = useState<string>();

  useEffect(() => {
    // The page polls on its own; a re-supplied initial snapshot only applies
    // if it is actually newer than what is already on screen.
    setSnapshot((current) => {
      const currentAt = Date.parse(current.generation.computedAt) || 0;
      const nextAt = Date.parse(initialSnapshot.generation.computedAt) || 0;
      return nextAt > currentAt ? initialSnapshot : current;
    });
  }, [initialSnapshot]);

  useEffect(() => {
    if (local) return;
    return startVisibilityPolling(async () => {
      try {
        const next = await loadHostedQueueSnapshot({ refresh: true });
        if (next?.entries) {
          setSnapshot(next);
          setRefreshError(false);
          setStaleSince(hostedDataStaleSince("queue"));
        }
      } catch {
        setRefreshError(true);
      }
    }, { runImmediately: true });
  }, [local]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(undefined), 6_000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  // Consume the completion marker once and strip it from the URL, so the PR
  // is not hidden forever while the tab sits on this address — the author may
  // re-request review on the same head.
  const [justCompleted] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const marker = { head: params.get("chewedHead"), pull: params.get("chewedPull") };
    if (["chewed", "chewedHead", "chewedPull", "selfReviewed"].some((name) => params.has(name))) {
      params.delete("chewed");
      params.delete("chewedHead");
      params.delete("chewedPull");
      params.delete("selfReviewed");
      const query = params.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    }
    return marker;
  });
  const ordered = useMemo(
    () => orderedEntries(snapshot.entries, lens, locallySkipped, justCompleted.head, justCompleted.pull),
    [justCompleted, lens, locallySkipped, snapshot.entries],
  );
  // Pin the card being read: a background re-rank must not swap its contents
  // mid-read, and a PR that momentarily reports "syncing" holds its place
  // instead of vanishing and reappearing.
  const pinnedKeyRef = useRef<string>(undefined);
  const pinned = pinnedKeyRef.current === undefined
    ? undefined
    : ordered.find((entry) => pullKey(entry.repository, entry.pullNumber) === pinnedKeyRef.current)
      ?? snapshot.entries.find((entry) =>
        pullKey(entry.repository, entry.pullNumber) === pinnedKeyRef.current && entry.forMe.reasonCode === "source-refreshing");
  const next = pinned ?? ordered[0];
  useEffect(() => {
    pinnedKeyRef.current = next ? pullKey(next.repository, next.pullNumber) : undefined;
  }, [next]);
  const rest = useMemo(
    () => next ? ordered.filter((entry) => pullKey(entry.repository, entry.pullNumber) !== pullKey(next.repository, next.pullNumber)) : [],
    [next, ordered],
  );
  const nextView = next && pullView(next, viewerLogin);
  const claimed = next?.forMe.outcome === "claimed-by-self";
  const skippedEntries = useMemo(() => snapshot.entries.filter((entry) => entry.forMe.skipped), [snapshot.entries]);
  // When nothing routes to the reviewer, say why rather than presenting the
  // tracked-but-unavailable PRs as if nothing exists.
  const unavailableBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const entry of snapshot.entries) {
      if (entry.forMe.outcome !== "ineligible" || entry.forMe.skipped) continue;
      const label = queueViewerStatus(entry);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [...counts.entries()].sort((left, right) => right[1] - left[1]);
  }, [snapshot.entries]);

  useEffect(() => {
    if (!next || local) return;
    const timer = window.setTimeout(() => void preloadHostedPull(next.repository, next.pullNumber).catch(() => undefined), 120);
    return () => window.clearTimeout(timer);
  }, [local, next]);

  const changeLens = (value: QueueLens) => {
    setLens(value);
    setSkipOpen(false);
    pinnedKeyRef.current = undefined;
    localStorage.setItem("chew:queue-lens", value);
  };

  /** Skipping always drops any claim first: nothing you set aside should stay reserved by you. */
  const skip = async (entry: QueueEntry, reason: string) => {
    setBusy(true);
    setSkipOpen(false);
    setActionError(undefined);
    pinnedKeyRef.current = undefined;
    try {
      if (local) {
        setLocallySkipped((current) => new Set(current).add(pullKey(entry.repository, entry.pullNumber)));
      } else {
        if (entry.forMe.outcome === "claimed-by-self") {
          await releaseHostedPullClaim({ repository: entry.repository, pullNumber: entry.pullNumber, headSha: entry.headSha }, "skipped");
        }
        await setHostedQueueSkip({ repository: entry.repository, pullNumber: entry.pullNumber, headSha: entry.headSha, reason }, true);
        setSnapshot(await loadHostedQueueSnapshot({ refresh: true }));
      }
      setLastSkipped(entry);
    } catch {
      setActionError("CHEW could not skip that review. The queue has not advanced.");
    } finally {
      setBusy(false);
    }
  };

  /** Hand a claim back without hiding the PR — it returns to the pool and stays in your queue. */
  const unclaim = async (entry: QueueEntry) => {
    setBusy(true);
    setSkipOpen(false);
    setActionError(undefined);
    pinnedKeyRef.current = undefined;
    try {
      if (!local) {
        await releaseHostedPullClaim({ repository: entry.repository, pullNumber: entry.pullNumber, headSha: entry.headSha });
        setSnapshot(await loadHostedQueueSnapshot({ refresh: true }));
      }
      setNotice(`Handed #${entry.pullNumber} back to the pool.`);
    } catch {
      setActionError("CHEW could not release that claim. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  const undoSkip = async () => {
    if (!lastSkipped) return;
    setBusy(true);
    setActionError(undefined);
    try {
      if (local) {
        setLocallySkipped((current) => {
          const nextSet = new Set(current);
          nextSet.delete(pullKey(lastSkipped.repository, lastSkipped.pullNumber));
          return nextSet;
        });
      } else {
        await setHostedQueueSkip({ repository: lastSkipped.repository, pullNumber: lastSkipped.pullNumber, headSha: lastSkipped.headSha }, false);
        setSnapshot(await loadHostedQueueSnapshot({ refresh: true }));
      }
      setLastSkipped(undefined);
    } catch {
      setActionError("CHEW could not restore that review. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  const restoreSkip = async (entry: QueueEntry) => {
    setBusy(true);
    setActionError(undefined);
    try {
      await setHostedQueueSkip({ repository: entry.repository, pullNumber: entry.pullNumber, headSha: entry.headSha }, false);
      setSnapshot(await loadHostedQueueSnapshot({ refresh: true }));
    } catch {
      setActionError("CHEW could not restore that review. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  const restoreAllSkipped = async () => {
    if (skippedEntries.length === 0) return;
    setBusy(true);
    setActionError(undefined);
    // The un-skips are independent; run them together and refresh whatever
    // landed, so a single failure neither serializes the rest nor hides the
    // partial success.
    const results = await Promise.allSettled(skippedEntries.map((entry) =>
      setHostedQueueSkip({ repository: entry.repository, pullNumber: entry.pullNumber, headSha: entry.headSha }, false)));
    const failed = results.filter((result) => result.status === "rejected").length;
    try {
      setSnapshot(await loadHostedQueueSnapshot({ refresh: true }));
      setLastSkipped(undefined);
    } catch {
      setRefreshError(true);
    } finally {
      setBusy(false);
    }
    if (failed > 0) {
      setActionError(`CHEW could not restore ${failed} of ${results.length} skipped ${results.length === 1 ? "review" : "reviews"}. Try again shortly.`);
    }
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']") || event.altKey || event.ctrlKey || event.metaKey) return;
      const key = event.key.toLocaleLowerCase();
      if (event.key === "1") changeLens("anything-useful");
      if (event.key === "2") changeLens("quick-wins");
      if (key === "q") {
        event.preventDefault();
        setQueueOpen((current) => !current);
      }
      if (key === "s" && next) {
        event.preventDefault();
        setSkipOpen((current) => !current);
      }
      if (key === "u" && next && claimed) {
        event.preventDefault();
        void unclaim(next);
      }
      if (nextView && (event.key === "ArrowRight" || (event.key === "Enter" && !target?.closest("button, a")))) {
        event.preventDefault();
        navigate(nextView.identity.reviewPath);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [claimed, next, nextView]);

  const center = (
    <div className="queue-lenses" aria-label="Queue lens">
      <button aria-pressed={lens === "anything-useful"} className={lens === "anything-useful" ? "active" : ""} onClick={() => changeLens("anything-useful")} type="button"><Sparkles aria-hidden="true" />Anything useful<kbd>1</kbd></button>
      <button aria-pressed={lens === "quick-wins"} className={lens === "quick-wins" ? "active" : ""} onClick={() => changeLens("quick-wins")} type="button"><Zap aria-hidden="true" />Quick wins<kbd>2</kbd></button>
    </div>
  );

  return (
    <AppShell active="queue" center={center} queueNext={next} queueSnapshot={snapshot}>
      <div className="queue-page">
        {refreshError && <p className="queue-notice is-warn">Queue refresh failed. Showing the last complete ranking.</p>}
        {staleSince && !refreshError && (
          <p className="queue-notice is-warn">CHEW cannot reach the server. Showing the queue saved {new Date(staleSince).toLocaleString()} — it keeps retrying.</p>
        )}
        {actionError && <p className="queue-notice is-error" role="alert">{actionError}</p>}
        {notice && <p className="queue-notice" role="status"><Check aria-hidden="true" />{notice}</p>}
        {lastSkipped && (
          <p className="queue-notice" role="status">
            <Check aria-hidden="true" />Passed on {repositoryShortName(lastSkipped.repository)}/{lastSkipped.pullNumber}.
            <button disabled={busy} onClick={() => { void undoSkip(); }} type="button"><RotateCcw aria-hidden="true" />Undo</button>
          </p>
        )}

        {next && nextView ? (
          <section aria-label="Your next review" className={`queue-brief channel-${nextView.channel}`}>
            <div className="queue-brief-head">
              <span className="queue-eyebrow">
                {nextView.channel === "mine" ? "Pick up where you left off" : nextView.channel === "owed" ? "Asked of you" : "Next for you"}
              </span>
              <span className="queue-brief-tally">
                {rest.length > 0 ? `${rest.length} more queued` : "nothing else queued"}
                {skippedEntries.length > 0 && ` · ${skippedEntries.length} passed`}
              </span>
            </div>

            <h1 className="queue-brief-title">{next.title}</h1>

            <p className="queue-brief-spec">
              <PullBadge facts={nextView.badge} size="md" />
              {nextView.holder?.isViewer && <span className="pv-holder is-you" title={nextView.holder.detail}>yours</span>}
              <PullMeasures view={nextView} />
            </p>

            {nextView.exceptions.length > 0 && (
              <p className="queue-brief-flags"><PullFlags exceptions={nextView.exceptions} verbose /></p>
            )}

            <p className="queue-brief-why">{queueReason(next)}</p>

            {next.areas.length > 0 && (
              <p className="queue-brief-areas">{next.areas.map((area) => <span key={area}>{area}</span>)}</p>
            )}

            <div className="queue-brief-actions">
              <a className="queue-take" href={nextView.identity.reviewPath} onClick={(event) => handleLocalNavigation(event, nextView.identity.reviewPath)}>
                {["claimed-by-self", "in-review-by-self"].includes(next.forMe.outcome) ? "Resume review" : "Start review"}
                <kbd aria-hidden="true">↵</kbd>
              </a>
              <div className="queue-pass">
                <button aria-expanded={skipOpen} disabled={busy} onClick={() => setSkipOpen((current) => !current)} type="button">
                  Pass<ChevronDown aria-hidden="true" /><kbd>S</kbd>
                </button>
                {skipOpen && (
                  <div className="queue-pass-menu">
                    <span className="queue-eyebrow">Why pass?</span>
                    {skipReasons.map((reason) => <button key={reason} onClick={() => { void skip(next, reason); }} type="button">{reason}</button>)}
                    <small>{claimed ? "Releases your claim and hides it until the author pushes." : "Hides it from your queue until the author pushes."}</small>
                  </div>
                )}
              </div>
              {claimed && (
                <button className="queue-release" disabled={busy} onClick={() => { void unclaim(next); }} type="button">
                  <Hand aria-hidden="true" />Hand back<kbd>U</kbd>
                </button>
              )}
              {next.url && (
                <a className="queue-brief-github" href={next.url} rel="noreferrer" target="_blank">GitHub<ExternalLink aria-hidden="true" /></a>
              )}
            </div>
          </section>
        ) : (
          <section className="queue-clear">
            <Inbox aria-hidden="true" />
            <h1>Nothing routes to you.</h1>
            <p>
              {skippedEntries.length > 0 || locallySkipped.size > 0
                ? `${skippedEntries.length || locallySkipped.size} you passed on ${(skippedEntries.length || locallySkipped.size) === 1 ? "is" : "are"} hidden until their authors push.`
                : "Every open pull request is either yours, taken, or already reviewed."}
            </p>
            {unavailableBreakdown.length > 0 && (
              <p className="queue-clear-breakdown">
                {unavailableBreakdown.map(([label, count]) => <span key={label}><b>{count}</b>{label.toLowerCase()}</span>)}
              </p>
            )}
            {locallySkipped.size > 0 && <button onClick={() => setLocallySkipped(new Set())} type="button">Bring passed PRs back</button>}
            {!local && skippedEntries.length > 0 && (
              <button disabled={busy} onClick={() => void restoreAllSkipped()} type="button">Bring {skippedEntries.length === 1 ? "it" : "them"} back</button>
            )}
          </section>
        )}

        {(rest.length > 0 || skippedEntries.length > 0) && (
          <section className="queue-rest">
            <button aria-expanded={queueOpen} className="queue-rest-toggle" onClick={() => setQueueOpen((current) => !current)} type="button">
              <ChevronDown aria-hidden="true" className={queueOpen ? "is-open" : undefined} />
              <span>{queueOpen ? "Hide your queue" : "See your whole queue"}</span>
              <kbd>Q</kbd>
            </button>
            {queueOpen && (
              <div className="queue-rest-body">
                {rest.length > 0 && (
                  <ol className="pv-cards">
                    {rest.map((entry, index) => (
                      <li key={pullKey(entry.repository, entry.pullNumber)}>
                        <PullCard
                          actions={<button className="pv-act" disabled={busy} onClick={() => { void skip(entry, "Not my area"); }} type="button">Pass</button>}
                          rank={index + 2}
                          view={pullView(entry, viewerLogin)}
                        />
                      </li>
                    ))}
                  </ol>
                )}
                {skippedEntries.length > 0 && (
                  <div className="queue-passed">
                    <header>
                      <strong className="queue-passed-title">Passed by you</strong>
                      <button disabled={busy} onClick={() => void restoreAllSkipped()} type="button"><RotateCcw aria-hidden="true" />Restore all</button>
                    </header>
                    <ol className="pv-cards">
                      {skippedEntries.map((entry) => (
                        <li key={pullKey(entry.repository, entry.pullNumber)}>
                          <PullCard
                            actions={<button className="pv-act" disabled={busy} onClick={() => { void restoreSkip(entry); }} type="button">Restore</button>}
                            view={pullView(entry, viewerLogin)}
                          />
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}
